import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Globe,
  Key,
  Shield,
  Save,
  Loader2,
  ExternalLink,
  Check,
  X,
  AlertCircle,
  Info,
  Plus,
  Trash2,
  Languages,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle,
  Settings,
  Mail,
  Send
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const AdminIntegrationSettings = () => {
  usePageTitle('API & Integrations');
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state - default to 'integrations' or get from URL
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'integrations');

  // Google OAuth Settings
  const [oauthEnabled, setOauthEnabled] = useState(false);
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [oauthCallbackUrl, setOauthCallbackUrl] = useState('');
  const [oauthClientIdSet, setOauthClientIdSet] = useState(false);
  const [oauthClientSecretSet, setOauthClientSecretSet] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(true);
  const [oauthSaving, setOauthSaving] = useState(false);

  // Google Translate Settings
  const [translateEnabled, setTranslateEnabled] = useState(false);
  const [translateApiKey, setTranslateApiKey] = useState('');
  const [translateProjectId, setTranslateProjectId] = useState('');
  const [translateApiKeySet, setTranslateApiKeySet] = useState(false);
  const [translateApiKeyPreview, setTranslateApiKeyPreview] = useState('');
  const [translateLoading, setTranslateLoading] = useState(true);
  const [translateSaving, setTranslateSaving] = useState(false);
  const [enabledLanguages, setEnabledLanguages] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  
  // Reserved Words Settings
  const [reservedWords, setReservedWords] = useState([]);
  const [newReservedWord, setNewReservedWord] = useState('');
  const [reservedWordsSaving, setReservedWordsSaving] = useState(false);
  
  // SendGrid Settings
  const [sendgridEnabled, setSendgridEnabled] = useState(false);
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [sendgridFromEmail, setSendgridFromEmail] = useState('');
  const [sendgridFromName, setSendgridFromName] = useState('');
  const [sendgridReplyEmail, setSendgridReplyEmail] = useState('');
  const [sendgridApiKeySet, setSendgridApiKeySet] = useState(false);
  const [sendgridApiKeyPreview, setSendgridApiKeyPreview] = useState('');
  const [sendgridLoading, setSendgridLoading] = useState(true);
  const [sendgridSaving, setSendgridSaving] = useState(false);
  const [sendgridTesting, setSendgridTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // API Keys Settings
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const primaryColor = theme?.primary_color || '#2B4B6A';

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    fetchSettings();
    fetchAllLanguages();
    fetchReservedWords();
    fetchSendGridSettings();
    fetchAPIKeys();
  }, []);

  const fetchReservedWords = async () => {
    try {
      const res = await api.get('/admin/integrations/google-translate/reserved-words');
      setReservedWords(res.data.reserved_words || []);
    } catch (error) {
      console.error('Error fetching reserved words:', error);
    }
  };

  const fetchAllLanguages = async () => {
    try {
      const res = await api.get('/integrations/all-available-languages');
      setAllLanguages(res.data.languages);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      // Fetch Google OAuth settings
      const oauthRes = await api.get('/admin/integrations/google-oauth');
      setOauthEnabled(oauthRes.data.enabled);
      setOauthClientIdSet(oauthRes.data.client_id_set);
      setOauthClientSecretSet(oauthRes.data.client_secret_set);
      setOauthCallbackUrl(oauthRes.data.callback_url || '');
      if (oauthRes.data.client_id_set) {
        setOauthClientId('********');
      }
      if (oauthRes.data.client_secret_set) {
        setOauthClientSecret('********');
      }
      setOauthLoading(false);

      // Fetch Google Translate settings
      const translateRes = await api.get('/admin/integrations/google-translate');
      setTranslateEnabled(translateRes.data.enabled);
      setTranslateApiKeySet(translateRes.data.api_key_set);
      setTranslateApiKeyPreview(translateRes.data.api_key_preview);
      setTranslateProjectId(translateRes.data.project_id || '');
      setEnabledLanguages(translateRes.data.enabled_languages || []);
      if (translateRes.data.api_key_set) {
        setTranslateApiKey('********');
      }
      setTranslateLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load integration settings');
      setOauthLoading(false);
      setTranslateLoading(false);
    }
  };

  const saveOAuthSettings = async () => {
    setOauthSaving(true);
    try {
      await api.put('/admin/integrations/google-oauth', {
        enabled: oauthEnabled,
        client_id: oauthClientId,
        client_secret: oauthClientSecret,
        callback_url: oauthCallbackUrl
      });
      toast.success('Google OAuth settings saved');
      // Refresh to get updated status
      const oauthRes = await api.get('/admin/integrations/google-oauth');
      setOauthClientIdSet(oauthRes.data.client_id_set);
      setOauthClientSecretSet(oauthRes.data.client_secret_set);
    } catch (error) {
      console.error('Error saving OAuth settings:', error);
      toast.error('Failed to save OAuth settings');
    } finally {
      setOauthSaving(false);
    }
  };

  const saveTranslateSettings = async () => {
    setTranslateSaving(true);
    try {
      await api.put('/admin/integrations/google-translate', {
        enabled: translateEnabled,
        api_key: translateApiKey,
        project_id: translateProjectId,
        enabled_languages: enabledLanguages
      });
      toast.success('Google Translate settings saved');
      // Refresh to get updated preview
      const translateRes = await api.get('/admin/integrations/google-translate');
      setTranslateApiKeySet(translateRes.data.api_key_set);
      setTranslateApiKeyPreview(translateRes.data.api_key_preview);
    } catch (error) {
      console.error('Error saving Translate settings:', error);
      toast.error('Failed to save Translate settings');
    } finally {
      setTranslateSaving(false);
    }
  };

  const toggleLanguage = (code) => {
    setEnabledLanguages(prev => 
      prev.includes(code) 
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const selectAllLanguages = () => {
    setEnabledLanguages(allLanguages.map(l => l.code));
  };

  const clearAllLanguages = () => {
    setEnabledLanguages([]);
  };

  const addReservedWord = () => {
    const word = newReservedWord.trim();
    if (!word) return;
    if (reservedWords.includes(word)) {
      toast.error('Word already in reserved list');
      return;
    }
    setReservedWords([...reservedWords, word]);
    setNewReservedWord('');
  };

  const removeReservedWord = (word) => {
    setReservedWords(reservedWords.filter(w => w !== word));
  };

  const saveReservedWords = async () => {
    setReservedWordsSaving(true);
    try {
      await api.put('/admin/integrations/google-translate/reserved-words', {
        reserved_words: reservedWords
      });
      toast.success('Reserved words saved successfully');
    } catch (error) {
      console.error('Error saving reserved words:', error);
      toast.error('Failed to save reserved words');
    } finally {
      setReservedWordsSaving(false);
    }
  };

  // SendGrid Functions
  const fetchSendGridSettings = async () => {
    try {
      const res = await api.get('/admin/integrations/sendgrid');
      setSendgridEnabled(res.data.enabled);
      setSendgridApiKeySet(res.data.api_key_set);
      setSendgridApiKeyPreview(res.data.api_key_preview);
      setSendgridFromEmail(res.data.from_email || '');
      setSendgridFromName(res.data.from_name || '');
      setSendgridReplyEmail(res.data.reply_email || '');
      if (res.data.api_key_set) {
        setSendgridApiKey('********');
      }
    } catch (error) {
      console.error('Error fetching SendGrid settings:', error);
    } finally {
      setSendgridLoading(false);
    }
  };

  const saveSendGridSettings = async () => {
    setSendgridSaving(true);
    try {
      await api.put('/admin/integrations/sendgrid', {
        enabled: sendgridEnabled,
        api_key: sendgridApiKey,
        from_email: sendgridFromEmail,
        from_name: sendgridFromName,
        reply_email: sendgridReplyEmail
      });
      toast.success('SendGrid settings saved');
      const res = await api.get('/admin/integrations/sendgrid');
      setSendgridApiKeySet(res.data.api_key_set);
      setSendgridApiKeyPreview(res.data.api_key_preview);
    } catch (error) {
      console.error('Error saving SendGrid settings:', error);
      toast.error('Failed to save SendGrid settings');
    } finally {
      setSendgridSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setSendgridTesting(true);
    try {
      const res = await api.post('/admin/integrations/sendgrid/test', { email: testEmail });
      toast.success(res.data.message);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendgridTesting(false);
    }
  };

  // API Keys Functions
  const fetchAPIKeys = async () => {
    try {
      const res = await api.get('/main-admin/api-keys');
      setApiKeys(res.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      setApiKeys([]);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const generateAPIKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setGenerating(true);
    try {
      const res = await api.post('/main-admin/api-keys', { name: newKeyName.trim() });
      setApiKeys(prev => [res.data, ...prev]);
      setNewKeyName('');
      setShowNewKeyForm(false);
      toast.success('API key generated successfully');
      setShowKeys(prev => ({ ...prev, [res.data.id]: true }));
    } catch (error) {
      console.error('Failed to generate API key:', error);
      toast.error('Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const revokeAPIKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/main-admin/api-keys/${keyId}`);
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      toast.success('API key revoked successfully');
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleShowKey = (keyId) => {
    setShowKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  // Tab definitions
  const tabs = [
    { id: 'integrations', label: 'Integrations', icon: Globe },
    { id: 'api-keys', label: 'API Keys', icon: Key },
  ];

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">API & Integrations</h1>
          <p className="text-gray-500 mt-1">Configure third-party integrations and manage API keys</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-current text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { color: primaryColor, borderColor: primaryColor } : {}}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Integrations Tab Content */}
        {activeTab === 'integrations' && (
          <>
        {/* Google OAuth Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                <Shield className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Google OAuth 2.0</h2>
                <p className="text-sm text-gray-500">Allow users to sign in with their Google account</p>
              </div>
            </div>
          </div>

          {oauthLoading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Google Login</label>
                  <p className="text-xs text-gray-500 mt-0.5">Show "Login with Google" button on sign-in page</p>
                </div>
                <button
                  onClick={() => setOauthEnabled(!oauthEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    oauthEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="oauth-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      oauthEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Client ID Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client ID
                  {oauthClientIdSet && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      <Check className="w-3 h-3 inline" /> Configured
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={oauthClientId}
                    onChange={(e) => setOauthClientId(e.target.value)}
                    placeholder={oauthClientIdSet ? "Enter new Client ID to replace" : "Enter your Google OAuth Client ID"}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="oauth-client-id-input"
                  />
                </div>
              </div>

              {/* Client Secret Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Secret
                  {oauthClientSecretSet && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      <Check className="w-3 h-3 inline" /> Configured
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={oauthClientSecret}
                    onChange={(e) => setOauthClientSecret(e.target.value)}
                    placeholder={oauthClientSecretSet ? "Enter new Client Secret to replace" : "Enter your Google OAuth Client Secret"}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="oauth-client-secret-input"
                  />
                </div>
              </div>

              {/* Callback URL Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Callback URL (Redirect URI)
                </label>
                <div className="relative">
                  <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={oauthCallbackUrl}
                    onChange={(e) => setOauthCallbackUrl(e.target.value)}
                    placeholder="https://yourdomain.com/auth/google/callback"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="oauth-callback-url-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  This URL must be added to your Google Cloud Console's "Authorized redirect URIs"
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Setup Instructions</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console → Credentials</a></li>
                      <li>Click "Create Credentials" → "OAuth 2.0 Client ID"</li>
                      <li>Select "Web application" as the application type</li>
                      <li>Add your domain to "Authorized JavaScript origins"</li>
                      <li>Add the Callback URL above to "Authorized redirect URIs"</li>
                      <li>Copy the Client ID and Client Secret here</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      <strong>Note:</strong> You must enable the "Google+ API" or "Google Identity" in your Google Cloud project.
                    </p>
                  </div>
                </div>
              </div>

              {/* Warning if enabled but credentials not set */}
              {oauthEnabled && (!oauthClientIdSet || !oauthClientSecretSet) && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Credentials Required</p>
                      <p className="mt-1">Google OAuth is enabled but credentials are not fully configured. Please enter your Client ID and Client Secret.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveOAuthSettings}
                  disabled={oauthSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="save-oauth-btn"
                >
                  {oauthSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save OAuth Settings
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Google Translate Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                <Globe className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Google Cloud Translation (V1.3)</h2>
                <p className="text-sm text-gray-500">Paid translation service for accurate, professional translations</p>
              </div>
            </div>
          </div>

          {translateLoading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Translation Service</label>
                  <p className="text-xs text-gray-500 mt-0.5">Use Google Cloud Translation API instead of free widget</p>
                </div>
                <button
                  onClick={() => setTranslateEnabled(!translateEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    translateEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="translate-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      translateEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* API Key Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                  {translateApiKeySet && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      (Set - ends with ...{translateApiKeyPreview})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={translateApiKey}
                    onChange={(e) => setTranslateApiKey(e.target.value)}
                    placeholder={translateApiKeySet ? "Enter new key to replace" : "Enter your Google Cloud API key"}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="translate-api-key-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Get your API key from the{' '}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    Google Cloud Console <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>

              {/* Project ID Input (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project ID <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={translateProjectId}
                  onChange={(e) => setTranslateProjectId(e.target.value)}
                  placeholder="your-project-id"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                  style={{ '--tw-ring-color': primaryColor }}
                  data-testid="translate-project-id-input"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Only required for advanced features. Found in your Google Cloud Console.
                </p>
              </div>

              {/* Warning if disabled but key set */}
              {!translateEnabled && translateApiKeySet && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Translation service is disabled</p>
                      <p className="mt-1">You have an API key configured but the service is disabled. Enable it above to use paid translations.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Language Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Available Languages
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllLanguages}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={clearAllLanguages}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Select which languages users can translate to. Leave empty to show all languages.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {allLanguages.map((lang) => (
                    <label
                      key={lang.code}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        enabledLanguages.includes(lang.code)
                          ? 'bg-blue-50 border border-blue-200'
                          : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={enabledLanguages.includes(lang.code)}
                        onChange={() => toggleLanguage(lang.code)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{lang.name}</span>
                    </label>
                  ))}
                </div>
                {enabledLanguages.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {enabledLanguages.length} language{enabledLanguages.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Setup Instructions</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Create or select a project</li>
                      <li>Enable the "Cloud Translation API"</li>
                      <li>Go to "APIs & Services" → "Credentials"</li>
                      <li>Create an API key and paste it above</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      <strong>Pricing:</strong> Google charges $20 per 1 million characters. First 500,000 characters free per month.
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveTranslateSettings}
                  disabled={translateSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="save-translate-btn"
                >
                  {translateSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Translation Settings
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SendGrid Email Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 mt-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                <Mail className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">SendGrid Email</h2>
                <p className="text-sm text-gray-500">Configure SendGrid for transactional emails (verification, notifications)</p>
              </div>
            </div>
          </div>

          {sendgridLoading ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable SendGrid</label>
                  <p className="text-xs text-gray-500 mt-0.5">Use SendGrid API for sending transactional emails</p>
                </div>
                <button
                  onClick={() => setSendgridEnabled(!sendgridEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    sendgridEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="sendgrid-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      sendgridEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* API Key Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                  {sendgridApiKeySet && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      <Check className="w-3 h-3 inline" /> Configured (ends with ...{sendgridApiKeyPreview})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={sendgridApiKey}
                    onChange={(e) => setSendgridApiKey(e.target.value)}
                    placeholder={sendgridApiKeySet ? "Enter new key to replace" : "Enter your SendGrid API key (SG.xxx...)"}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="sendgrid-api-key-input"
                  />
                </div>
              </div>

              {/* From Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={sendgridFromEmail}
                    onChange={(e) => setSendgridFromEmail(e.target.value)}
                    placeholder="noreply@yourdomain.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="sendgrid-from-email-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  The email address that will appear as the sender
                </p>
              </div>

              {/* From Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Name <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={sendgridFromName}
                  onChange={(e) => setSendgridFromName(e.target.value)}
                  placeholder="Trados"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                  style={{ '--tw-ring-color': primaryColor }}
                  data-testid="sendgrid-from-name-input"
                />
              </div>

              {/* Reply Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reply-To Email <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={sendgridReplyEmail}
                    onChange={(e) => setSendgridReplyEmail(e.target.value)}
                    placeholder="support@yourdomain.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    data-testid="sendgrid-reply-email-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Where replies will be directed. Defaults to From Email if empty.
                </p>
              </div>

              {/* Warning if enabled but key not set */}
              {sendgridEnabled && !sendgridApiKeySet && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">API Key Required</p>
                      <p className="mt-1">SendGrid is enabled but no API key is configured. Please enter your SendGrid API key to send emails.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Setup Instructions</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to <a href="https://app.sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">SendGrid Dashboard</a></li>
                      <li>Navigate to Settings → API Keys</li>
                      <li>Click "Create API Key" with "Full Access" or "Restricted Access" (Mail Send permission)</li>
                      <li>Copy the generated key (starts with SG.) and paste it above</li>
                      <li>Verify your sender domain or email in "Sender Authentication"</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Test Email */}
              {sendgridApiKeySet && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Send Test Email</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="Enter email to send test to..."
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                      style={{ '--tw-ring-color': primaryColor }}
                      data-testid="sendgrid-test-email-input"
                    />
                    <button
                      onClick={sendTestEmail}
                      disabled={sendgridTesting}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition disabled:opacity-50"
                      data-testid="sendgrid-test-btn"
                    >
                      {sendgridTesting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Send Test
                    </button>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveSendGridSettings}
                  disabled={sendgridSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="save-sendgrid-btn"
                >
                  {sendgridSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save SendGrid Settings
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Reserved Words Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                <Languages className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Reserved Words</h2>
                <p className="text-sm text-gray-500">Words that should NOT be translated (company names, brands, etc.)</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              <p>Add words or phrases that should remain in their original language. Examples:</p>
              <ul className="list-disc list-inside mt-2 text-gray-500 space-y-1">
                <li>Company names (e.g., "Trados", "Wave Trading")</li>
                <li>Brand names and product names</li>
                <li>Technical terms that shouldn't be translated</li>
              </ul>
              <p className="mt-2 text-xs text-gray-400">
                Note: Emails and URLs are automatically excluded from translation.
              </p>
            </div>

            {/* Add new word */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newReservedWord}
                onChange={(e) => setNewReservedWord(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addReservedWord()}
                placeholder="Enter word or phrase to reserve..."
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                style={{ '--tw-ring-color': primaryColor }}
                data-testid="reserved-word-input"
              />
              <button
                onClick={addReservedWord}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
                data-testid="add-reserved-word-btn"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>

            {/* Current reserved words */}
            {reservedWords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {reservedWords.map((word, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm"
                  >
                    <span className="notranslate" translate="no">{word}</span>
                    <button
                      onClick={() => removeReservedWord(word)}
                      className="text-gray-400 hover:text-red-500 transition"
                      data-testid={`remove-reserved-word-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No reserved words configured</p>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <button
                onClick={saveReservedWords}
                disabled={reservedWordsSaving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-reserved-words-btn"
              >
                {reservedWordsSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Reserved Words
              </button>
            </div>
          </div>
        </div>
          </>
        )}

        {/* API Keys Tab Content */}
        {activeTab === 'api-keys' && (
          <>
            {/* Generate New Key Button */}
            <div className="flex justify-end mb-6">
              <button
                onClick={() => setShowNewKeyForm(true)}
                data-testid="generate-api-key-btn"
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-4 h-4" />
                Generate API Key
              </button>
            </div>

            {/* New Key Form */}
            {showNewKeyForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate New API Key</h3>
                <div className="flex gap-4">
                  <input
                    type="text"
                    data-testid="api-key-name-input"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Enter a name for this API key (e.g., 'Mobile App', 'Partner Integration')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={generateAPIKey}
                    disabled={generating}
                    data-testid="confirm-generate-api-key"
                    className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Key className="w-4 h-4" />
                        Generate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewKeyForm(false);
                      setNewKeyName('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  API keys allow external applications to access your platform's API. Keep them secure!
                </p>
              </div>
            )}

            {/* API Keys List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Active API Keys</h2>
                
                {apiKeysLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : apiKeys.length === 0 ? (
                  <div className="text-center py-12">
                    <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys</h3>
                    <p className="text-gray-500 mb-4">Generate an API key to enable external integrations.</p>
                    <button
                      onClick={() => setShowNewKeyForm(true)}
                      className="font-medium hover:opacity-80"
                      style={{ color: primaryColor }}
                    >
                      Generate your first API key
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div
                        key={key.id}
                        data-testid={`api-key-${key.id}`}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{key.name}</h4>
                            <p className="text-sm text-gray-500">
                              Created: {new Date(key.created_at).toLocaleDateString()} •
                              Last used: {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                          <button
                            onClick={() => revokeAPIKey(key.id)}
                            data-testid={`revoke-api-key-${key.id}`}
                            className="flex items-center gap-1 px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Revoke
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-gray-100 px-3 py-2 rounded font-mono text-sm">
                            {showKeys[key.id] ? key.key : '••••••••••••••••••••••••••••••••'}
                          </code>
                          <button
                            onClick={() => toggleShowKey(key.id)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title={showKeys[key.id] ? 'Hide key' : 'Show key'}
                          >
                            {showKeys[key.id] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(key.key, key.id)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copy to clipboard"
                          >
                            {copiedKey === key.id ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <Copy className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* API Documentation Info */}
            <div className="mt-6 bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">API Usage</h3>
              <p className="text-gray-600 mb-4">
                Include your API key in requests using the <code className="bg-gray-200 px-2 py-0.5 rounded">X-API-Key</code> header:
              </p>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X GET "${window.location.origin}/api/v1/endpoint" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json"`}
              </pre>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminIntegrationSettings;
